import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { UserEntity } from 'sonata-static-storage/lib/entities';

import { useAuth } from '@/context/AuthProvider';
import { hasRoutePermission } from '@/helpers/permissions/routePermissions.helper';
import {
  isAllowedSharedPath,
  isSharedFullAccessNavigationAllowed,
  isSharedReportSession
} from '@/helpers/shareReport/shareReport.helper';
import { useRedirectToLogin, useRedirectToMain, useRedirectToRoot } from '@/hooks/redirectTo.hook';
import { useGlobalStore } from '@/store/global';
import { getSession } from '@/store/global/session/selectors';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type GuardType = 'authenticated' | 'unauthenticated';

function isAllowedToAccessAppMainRoute(
  customPermissions: UserInterface['customPermissions'],
  userRole: string,
  userAssociatedRole?: UserRoleInterface
): boolean {
  const v2Permission = 'v2';
  const isAdminWithPermissions =
    userRole === 'admin' && isEmpty(customPermissions?.routes) && isEmpty(userAssociatedRole?.routesAccess);
  const isUserWithProperCustomization =
    !isEmpty(customPermissions?.routes) && customPermissions?.routes?.includes(v2Permission);
  const isUserWithDefaultRoleProperPermissions =
    isEmpty(customPermissions?.routes) && !!userAssociatedRole?.routesAccess.includes(v2Permission);
  return isAdminWithPermissions || isUserWithProperCustomization || isUserWithDefaultRoleProperPermissions;
}

function ProtectedRoute({
  children,
  guardType,
  redirectPath = '/'
}: {
  children: React.ReactElement;
  guardType?: GuardType;
  redirectPath?: string;
}) {
  const redirectToLogin = useRedirectToLogin();
  const redirectToMain = useRedirectToMain();
  const redirectToRoot = useRedirectToRoot();
  const [prevGuarType, setPrevGuardType] = useState(guardType);
  const { refetch, status } = useAuth();
  const location = useLocation();
  const session = useGlobalStore(getSession);
  const userEntity = useGlobalStore(getUser);
  const user = userEntity?.toJSON?.() as UserInterface;
  let customPermissions = UserEntity.defaultValues.customPermissions!;
  let userFrom: UserInterface['userFrom'];
  let userRole: UserInterface['userRole'] = 'user';
  if (user) {
    ({ customPermissions, userFrom, userRole } = user);
  }
  const userRoles = useGlobalStore(getUserRoles) as UserRoleInterface[];
  const associatedUserRole = userRoles.find(({ code }) => code === userRole);

  if (guardType && prevGuarType !== guardType) {
    setPrevGuardType(guardType);
    if (['authenticated', 'unauthenticated'].includes(guardType)) {
      refetch?.();
    }
  }

  const isAllowedByAuthStatus = {
    authenticated: status === 'authenticated',
    unauthenticated: status === 'unauthenticated'
  }[guardType || 'authenticated'];

  const effectiveCustomPermissions =
    userFrom?.customPermissions || (customPermissions as UserInterface['customPermissions']);
  const effectiveUserRole = userFrom?.userRole || userRole;
  const effectiveAssociatedRole = userFrom
    ? userRoles.find(({ code }) => code === userFrom?.userRole)
    : associatedUserRole;
  const hasV2Access = isAllowedToAccessAppMainRoute(
    effectiveCustomPermissions,
    effectiveUserRole,
    effectiveAssociatedRole
  );

  useEffect(() => {
    if (isSharedReportSession(session)) {
      // The shared session is confined to the access route + the locked report, and the
      // share user is purpose-built to view it. Skip the normal v2/route-permission
      // redirects on those paths. Any other path either needs "full access" navigation
      // (server-validated, within the share's own entities) or tears the session down.
      if (isAllowedSharedPath(location.pathname)) {
        return;
      }
      let cancelled = false;
      isSharedFullAccessNavigationAllowed(session, location.pathname).then(allowed => {
        if (!cancelled && !allowed) {
          redirectToLogin(location);
        }
      });
      return () => {
        cancelled = true;
      };
    }
    if (!isAllowedByAuthStatus && guardType === 'authenticated') {
      redirectToLogin(location);
    }
    if (!isAllowedByAuthStatus && guardType === 'unauthenticated') {
      redirectToMain(location);
    }
    if (isAllowedByAuthStatus && guardType === 'authenticated' && !hasV2Access) {
      redirectToLogin(location);
    }
    if (
      isAllowedByAuthStatus &&
      guardType === 'authenticated' &&
      !hasRoutePermission(location.pathname, user, userRoles)
    ) {
      redirectToRoot();
    }
  }, [
    isAllowedByAuthStatus,
    guardType,
    redirectToLogin,
    hasV2Access,
    redirectToMain,
    location,
    userEntity,
    userRoles,
    session
  ]);

  if (!isAllowedByAuthStatus && guardType === 'unauthenticated') {
    return <Navigate state={{ from: location }} to={redirectPath} />;
  }

  return children;
}

export default ProtectedRoute;
